import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "@helpers/translate";
import {
  showRiskValidationError,
} from "@actions/futures/positionNotifications";
import { formatPrice } from "@helpers/formatPrice";
import useBodyScrollLock from "@helpers/useBodyScrollLock";
import TradeArrowUpIcon from "@assets/icons/TradeArrowUpIcon";
import TradeArrowDownIcon from "@assets/icons/TradeArrowDownIcon";
import InfoCircleIcon from "@assets/icons/InfoCircleIcon";
import CrossIcon from "@assets/icons/CrossIcon";
import DeleteLeftIcon from "@assets/icons/DeleteLeftIcon";
import SwapVerticalIcon from "@assets/icons/SwapVerticalIcon";
import { TradeButtons } from "@components/pages/Futures/components/TradeButtons";
import {
  getPairMeta,
  getPairTicker,
} from "@components/pages/Futures/components/Quotes/pairMeta";
import {
  LONG_COLOR,
  MIN_ORDER_COST_USDT,
  SHORT_COLOR,
} from "../TradeForm/constants";
import { useRiskSettings } from "../TradeForm/hooks/useRiskSettings";
import { formatUsd, groupAmount } from "../TradeForm/utils/format";
import { blurOnEnter } from "../TradeForm/utils/input";
import { LeveragePicker } from "./components/LeveragePicker";
import { FeesModal } from "../../TradeModals";
import { useRiskKeypad } from "./hooks/useRiskKeypad";
import { useWagerKeypad } from "./hooks/useWagerKeypad";
import { KEYPAD_DELETE, KEYPAD_KEYS } from "./utils/keypad";
import "./MobileTradeForm.sass";

const PAGE_WAGER = 0;
const PAGE_RISK = 1;
const SWIPE_THRESHOLD = 50;
const WHEEL_THRESHOLD = 40;
// Trackpad gestures keep emitting wheel events (inertia); this pause ends one
const WHEEL_GESTURE_END_DELAY = 200;

// Spacers shrink only slightly on short screens, beyond that the sheet scrolls
const SPACER_MIN_SCALE = 0.8;

// The leverage wheel scrolls horizontally on its own
const isInsideLeveragePicker = (target) =>
  Boolean(target.closest?.(".mobile-trade__leverage-picker"));

const getAmountModifier = (text) => {
  if (text.length > 10) return " mobile-trade__amount--small";
  if (text.length > 7) return " mobile-trade__amount--medium";

  return "";
};

/**
 * Vertical gap from the design that shrinks a little on short screens,
 * the sheet scrolls once it can't shrink further.
 */
const Spacer = ({ size, grow = false }) => (
  <span
    className={`mobile-trade__spacer${grow ? " mobile-trade__spacer--grow" : ""}`}
    style={{ flexBasis: size, minHeight: size * SPACER_MIN_SCALE }}
    aria-hidden="true"
  />
);

/**
 * Big keypad-driven amount with a muted unit before or after it.
 */
const Amount = ({
  text,
  prefix,
  suffix,
  label,
  inputRef,
  isError = false,
  isSelected = false,
  ...inputProps
}) => (
  <label
    className={`mobile-trade__amount${getAmountModifier(text)}${isError ? " mobile-trade__amount--error" : ""}${isSelected ? " mobile-trade__amount--selected" : ""}`}
  >
    {prefix && <span className="mobile-trade__amount-unit">{prefix}</span>}
    {/* Hidden copy of the text sizes the input to the exact glyph width */}
    <span className="mobile-trade__amount-field">
      <span className="mobile-trade__amount-sizer" aria-hidden="true">
        {text}
      </span>
      <input
        ref={inputRef}
        className="mobile-trade__amount-value"
        value={text}
        size={1}
        readOnly
        inputMode="none"
        tabIndex={-1}
        aria-label={label}
        {...inputProps}
      />
    </span>
    {suffix && <span className="mobile-trade__amount-unit">{suffix}</span>}
  </label>
);

const Pill = ({ label, value }) => (
  <p className="mobile-trade__pill">
    <span className="mobile-trade__pill-label">{label}</span>
    <span className="mobile-trade__pill-value">{value}</span>
  </p>
);

const WagerPage = ({
  draft,
  isOvertype,
  wager,
  availableBalance,
  leverage,
  onLeverageChange,
  liquidationPrice,
}) => {
  const { __ } = useTranslation();
  const isWagerError = wager < MIN_ORDER_COST_USDT && availableBalance > 0;

  return (
    <>
      <p className="mobile-trade__available">
        <span className="mobile-trade__available-label">
          {__("futures.available_balance")}:
        </span>
        <span className="mobile-trade__available-value">
          {formatUsd(availableBalance)}
        </span>
      </p>

      <Spacer size={23} />

      <Amount
        text={groupAmount(draft) || "0"}
        prefix="$"
        label={__("futures.mobile_trade.wager")}
        isError={isWagerError}
        isSelected={isOvertype}
      />

      {isWagerError && (
        <p className="mobile-trade__wager-error" role="alert">
          <InfoCircleIcon size={14} color={SHORT_COLOR} />
          {__("futures.validation.wager_min").replace(
            "{min}",
            formatUsd(MIN_ORDER_COST_USDT),
          )}
        </p>
      )}

      <Spacer size={32} />

      <div className="mobile-trade__leverage">
        <LeveragePicker value={leverage} onChange={onLeverageChange} />

        <p className="mobile-trade__leverage-label">
          {__("futures.leverage")}
          <InfoCircleIcon size={12} />
        </p>
      </div>

      <Spacer size={44} />

      <Pill
        label={`${__("futures.liquidation_price")}:`}
        value={formatUsd(liquidationPrice)}
      />
    </>
  );
};

const RiskPage = ({ risk, riskKeypad, wager }) => {
  const { __ } = useTranslation();
  const isPercent = risk.mode === "%";
  const activeLabel = riskKeypad.isTp
    ? __("futures.tp_sl.take_profit")
    : __("futures.tp_sl.stop_loss");
  const otherLabel = riskKeypad.isTp
    ? __("futures.tp_sl.stop_loss")
    : __("futures.tp_sl.take_profit");
  const otherValue = isPercent
    ? `${groupAmount(riskKeypad.otherValue)}%`
    : `$${groupAmount(riskKeypad.otherValue)}`;

  return (
    <>
      <div className="mobile-trade__mode" role="group">
        {["$", "%"].map((mode) => (
          <button
            key={mode}
            type="button"
            className={`mobile-trade__mode-btn${risk.mode === mode ? " mobile-trade__mode-btn--active" : ""}`}
            aria-pressed={risk.mode === mode}
            onClick={() => riskKeypad.switchMode(mode)}
          >
            {mode}
          </button>
        ))}
      </div>

      <Spacer size={14} />

      <p className="mobile-trade__risk-label">{activeLabel}</p>

      <Spacer size={23} />

      <Amount
        text={groupAmount(riskKeypad.draft)}
        inputRef={riskKeypad.inputRef}
        onFocus={riskKeypad.onFocus}
        onBlur={riskKeypad.onBlur}
        onKeyDown={blurOnEnter}
        prefix={isPercent ? null : "$"}
        suffix={isPercent ? "%" : null}
        label={activeLabel}
        isSelected={riskKeypad.isOvertype}
      />

      <Spacer size={22} />

      <button
        type="button"
        className="mobile-trade__risk-swap"
        onClick={riskKeypad.swapField}
      >
        <SwapVerticalIcon />
        <span className="mobile-trade__risk-swap-label">{otherLabel}</span>
        <span className="mobile-trade__risk-swap-value">{otherValue}</span>
      </button>

      <Spacer size={32} />

      <div className="mobile-trade__save-risk">
        <span>{__("futures.mobile_trade.save_risk")}</span>
        <button
          type="button"
          className={`mobile-trade__switch${risk.saveRisk ? " mobile-trade__switch--on" : ""}`}
          role="switch"
          aria-checked={risk.saveRisk}
          aria-label={__("futures.mobile_trade.save_risk")}
          onClick={risk.toggleSaveRisk}
        >
          <span className="mobile-trade__switch-knob" />
        </button>
      </div>

      <Spacer size={10} />

      <Pill
        label={__("futures.mobile_trade.wager_amount")}
        value={formatUsd(wager)}
      />
    </>
  );
};

const MobileTradeSheet = ({
  pair,
  isLong,
  leverage,
  onLeverageChange,
  wager,
  onWagerChange,
  availableBalance,
  liquidationPrice,
  entryPrice,
  risk,
  onPlaceTrade,
  isPlacingTrade,
  onClose,
}) => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(PAGE_WAGER);
  const [isFeesOpen, setIsFeesOpen] = useState(false);
  const swipeStartRef = useRef(null);
  const wheelGestureRef = useRef({ deltaX: 0, isHandled: false, timer: null });
  const wagerKeypad = useWagerKeypad({
    wager,
    onWagerChange,
    availableBalance,
  });
  const riskKeypad = useRiskKeypad(risk);

  useBodyScrollLock(true);

  const pairMeta = getPairMeta(pair?.slug);
  const baseAsset = getPairTicker(pair?.slug);
  const DirectionIcon = isLong ? TradeArrowUpIcon : TradeArrowDownIcon;
  const onKeyPress =
    page === PAGE_WAGER ? wagerKeypad.onKeyPress : riskKeypad.onKeyPress;

  const goToPage = (next) => {
    if (next === page) return;

    // A swipe doesn't move focus, blur so a TP/SL edit is committed before unmount
    document.activeElement?.blur?.();
    setPage(next);
  };

  useEffect(() => () => clearTimeout(wheelGestureRef.current.timer), []);

  // Finger swipe on touch screens, drag with a mouse
  const handlePointerDown = (event) => {
    // A tap anywhere but the keypad drops the overtype selection
    if (!event.target.closest?.(".mobile-trade__keypad")) {
      if (page === PAGE_WAGER) wagerKeypad.dismissOvertype();
      else riskKeypad.dismissOvertype();
    }

    swipeStartRef.current = isInsideLeveragePicker(event.target)
      ? null
      : { x: event.clientX, y: event.clientY };
  };

  const handlePointerUp = (event) => {
    const start = swipeStartRef.current;

    swipeStartRef.current = null;

    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;

    goToPage(dx < 0 ? PAGE_RISK : PAGE_WAGER);
  };

  // Two-finger horizontal swipe on a laptop trackpad
  const handleWheel = (event) => {
    if (isInsideLeveragePicker(event.target)) return;
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

    const gesture = wheelGestureRef.current;

    clearTimeout(gesture.timer);
    gesture.timer = setTimeout(() => {
      gesture.deltaX = 0;
      gesture.isHandled = false;
    }, WHEEL_GESTURE_END_DELAY);

    if (gesture.isHandled) return;

    gesture.deltaX += event.deltaX;

    if (Math.abs(gesture.deltaX) < WHEEL_THRESHOLD) return;

    gesture.isHandled = true;
    goToPage(gesture.deltaX > 0 ? PAGE_RISK : PAGE_WAGER);
  };

  const buildErrorText = (error) => {
    if (!error.textParams) return __(error.textKey);

    const { min, max, mode } = error.textParams;
    const fmt = (v) => (mode === "%" ? `${v}%` : `$${v}`);

    return __(error.textKey).replace("{min}", fmt(min)).replace("{max}", fmt(max));
  };

  const handlePlaceTrade = async () => {
    riskKeypad.formatAll();

    const error = risk.getValidationError();

    if (error) {
      dispatch(
        showRiskValidationError({
          title: __(error.titleKey),
          text: buildErrorText(error),
        }),
      );
      return;
    }

    if (await onPlaceTrade()) onClose();
  };

  return (
    <div
      className="mobile-trade"
      role="dialog"
      aria-modal="true"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        swipeStartRef.current = null;
      }}
      onWheel={handleWheel}
    >
      <header className="mobile-trade__header">
        <div className="mobile-trade__coin">
          {pairMeta?.icon ? (
            <img
              src={pairMeta.icon}
              alt={baseAsset || "asset"}
              className="mobile-trade__coin-icon"
            />
          ) : (
            <span className="mobile-trade__coin-icon" />
          )}

          <span className="mobile-trade__coin-badge">
            <DirectionIcon
              size={16}
              color={isLong ? LONG_COLOR : SHORT_COLOR}
            />
          </span>
        </div>

        <p className="mobile-trade__pair">
          <span className="mobile-trade__pair-label">{baseAsset}</span>
          <span className="mobile-trade__pair-price">
            ${groupAmount(formatPrice(entryPrice))}
          </span>
        </p>

        <button
          type="button"
          className="mobile-trade__close"
          onClick={onClose}
          aria-label={__("futures.mobile_trade.close")}
        >
          <CrossIcon />
        </button>
      </header>

      <Spacer size={54} />

      {page === PAGE_WAGER ? (
        <WagerPage
          draft={wagerKeypad.draft}
          isOvertype={wagerKeypad.isOvertype}
          wager={wager}
          availableBalance={availableBalance}
          leverage={leverage}
          onLeverageChange={onLeverageChange}
          liquidationPrice={liquidationPrice}
        />
      ) : (
        <RiskPage risk={risk} riskKeypad={riskKeypad} wager={wager} />
      )}

      {/* Grows so the pager and keypad stay in place on both pages */}
      <Spacer size={20} grow />

      <div className="mobile-trade__pager">
        {[
          [PAGE_WAGER, __("futures.mobile_trade.wager")],
          [PAGE_RISK, __("futures.tp_sl.column")],
        ].map(([pageIndex, label]) => (
          <button
            key={pageIndex}
            type="button"
            className={`mobile-trade__pager-dot${page === pageIndex ? " mobile-trade__pager-dot--active" : ""}`}
            aria-label={label}
            aria-current={page === pageIndex}
            onClick={() => goToPage(pageIndex)}
          />
        ))}
      </div>

      <Spacer size={28} />

      <div className="mobile-trade__keypad">
        {KEYPAD_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="mobile-trade__key"
            // Keep focus in the TP/SL input while typing
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onKeyPress(key)}
            aria-label={key === KEYPAD_DELETE ? __("common.delete") : key}
          >
            {key === KEYPAD_DELETE ? <DeleteLeftIcon /> : key}
          </button>
        ))}
      </div>

      <Spacer size={48} />

      <button
        type="button"
        className={`mobile-trade__submit${isPlacingTrade ? " mobile-trade__submit--loading" : ""}`}
        onClick={handlePlaceTrade}
        disabled={isPlacingTrade || wager < MIN_ORDER_COST_USDT}
      >
        {isPlacingTrade
          ? __("futures.mobile_trade.placing")
          : __("futures.mobile_trade.place_trade")}
      </button>

      <p className="mobile-trade__fees">
        {__("futures.mobile_trade.fees_note")}{" "}
        <button
          type="button"
          className="mobile-trade__fees-link"
          onClick={() => setIsFeesOpen(true)}
        >
          {__("futures.mobile_trade.fees_link")}
        </button>
        .
      </p>

      <FeesModal
        isOpen={isFeesOpen}
        onClose={() => setIsFeesOpen(false)}
        wager={wager}
        leverage={leverage}
        pair={pair}
      />
    </div>
  );
};

/**
 * Mobile trade flow: Long/Short buttons open a full-screen sheet with
 * wager/leverage and TP/SL pages. Takes the same props as TradeForm.
 */
export const MobileTradeForm = ({
  direction,
  onDirectionChange,
  wager,
  entryPrice,
  setTakeProfitPercent,
  setStopLossPercent,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isLong = direction !== "short";

  // Lives outside the sheet so TP/SL keep syncing like on desktop
  const risk = useRiskSettings({
    wager,
    setTakeProfitPercent,
    setStopLossPercent,
  });

  const handleSelect = (next) => {
    onDirectionChange(next);
    setIsOpen(true);
  };

  return (
    <>
      <TradeButtons onSelect={handleSelect} />

      {isOpen &&
        createPortal(
          <MobileTradeSheet
            {...props}
            wager={wager}
            entryPrice={entryPrice}
            isLong={isLong}
            risk={risk}
            onClose={() => setIsOpen(false)}
          />,
          document.body,
        )}
    </>
  );
};
